import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import { authService, UniqCampLogo, isValidEmail, SCHOOL_PORTAL_SESSION_KEYS } from '@uniqcamp/shared';
import { useToast } from '../contexts/ToastContext';

const Login: React.FC = () => {
  const [schoolCode, setSchoolCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SCHOOL_PORTAL_SESSION_KEYS.LOGIN_NOTICE);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { message?: string };
      sessionStorage.removeItem(SCHOOL_PORTAL_SESSION_KEYS.LOGIN_NOTICE);
      if (parsed?.message) showToast('error', parsed.message);
    } catch {
      sessionStorage.removeItem(SCHOOL_PORTAL_SESSION_KEYS.LOGIN_NOTICE);
    }
  }, [showToast]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const code = schoolCode.trim().toUpperCase();
      const result = await authService.loginWithSchool(code, email.trim().toLowerCase(), password);
      if (result.success) {
        navigate('/');
      } else {
        showToast('error', result.message || 'Login failed');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      showToast('error', e.response?.data?.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = Boolean(schoolCode.trim() && password && isValidEmail(email));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left cover */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          background: 'linear-gradient(135deg, #4f227d 0%, #371755 50%, #2d1147 100%)',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Box sx={{ color: 'white', textAlign: 'center', maxWidth: 380 }}>
          <SchoolIcon sx={{ fontSize: 64, mb: 2, opacity: 0.95 }} />
          <Box component="h1" sx={{ m: 0, display: 'flex', justifyContent: 'center', mb: 1 }}>
            <UniqCampLogo style={{ maxWidth: 280, maxHeight: 80, width: 'auto' }} />
          </Box>
          <Typography variant="h6" sx={{ mt: 2, opacity: 0.9, fontWeight: 500 }}>
            Staff Portal
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, opacity: 0.8 }}>
            Sign in with your school code, email and password
          </Typography>
        </Box>
      </Box>

      {/* Right form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your school code, email and password to sign in
          </Typography>

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="School Code"
              value={schoolCode}
              onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
              margin="normal"
              required
              placeholder="e.g. ABC123"
              inputProps={{ maxLength: 6 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              placeholder="you@school.com"
              autoComplete="email"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !canSubmit}
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2" underline="hover">
                Forgot password?
              </Link>
            </Box>
          </form>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            First time? <Link component={RouterLink} to="/register" underline="hover">Register your school</Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
